import { BriefcaseBusiness, Clock, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { getCompanyColor, type CompanyColor } from '../utils/companyColor'
import type { Shift } from '@/types/shift.types'

export interface ShiftTemplateCardProps {
  shift: Shift
  companyName: string
  /** Pre-resolved color (avoids re-hashing in the parent loop). Falls back to companyId hash. */
  color?: CompanyColor
  companyId?: string
  canUpdate: boolean
  canDelete: boolean
  onEdit: () => void
  onDelete: () => void
}

export function ShiftTemplateCard({
  shift,
  companyName,
  color,
  companyId,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}: ShiftTemplateCardProps): React.ReactElement {
  const c = color ?? getCompanyColor(companyId ?? '')

  return (
    <div
      className={cn(
        'group relative h-[118px] rounded-lg border border-gray-200 bg-white p-3 pt-3.5 shadow-[0_1px_3px_rgba(15,23,42,0.05)] transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800'
      )}
    >
      <span className="absolute inset-x-0 top-0 h-0.5 rounded-t-lg" style={{ backgroundColor: c.dot }} aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn('mt-4 flex h-7 w-7 shrink-0 items-center justify-center rounded-full', c.bg, c.text)}
        >
          <BriefcaseBusiness className="h-3.5 w-3.5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn('text-xs font-semibold', c.text)}>{companyName}</p>
          <p className="mt-0.5 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {shift.name}
          </p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            {shift.startTime} – {shift.endTime}
          </p>
          {shift.workDays?.length ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {shift.workDays.map((d) => d.slice(0, 3)).join(', ')}
            </p>
          ) : null}
        </div>
        {/* Actions sit directly on the card (no menu hop). Always visible on touch,
            revealed on hover/keyboard focus on pointer devices to keep cards calm. */}
        {(canUpdate || canDelete) && (
          <div className="flex shrink-0 items-center gap-0.5 sm:opacity-0 sm:transition-opacity sm:focus-within:opacity-100 sm:group-hover:opacity-100">
            {canUpdate ? (
              <button
                type="button"
                onClick={onEdit}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label={`Edit ${shift.name}`}
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                onClick={onDelete}
                className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                aria-label={`Delete ${shift.name}`}
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

ShiftTemplateCard.displayName = 'ShiftTemplateCard'
